import { useHistory, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRightLeft } from "lucide-react";

import { getSupportedMimeType, URLS, usePlayerSettings, useSocialCoachSession } from "@/utils";
import { ControlSection } from "./components/ControlsSection";
import { VideoCanvas } from "./components/VideoCanvas";
import { TeleprompterScript } from "./components/TeleprompterScript";
import { ScreenCaptureToggle } from "./components/ScreenCaptureToggle";
import { UploadingOverlay } from "./components/UploadingOverlay";

import { useRecorderSettings } from "./hooks/useRecorderSettings";
import { useDeviceManager } from "./hooks/useDeviceManager";
import { useVirtualBackground } from "./hooks/useVirtualBackground";
import { useTeleprompterScroll } from "./hooks/useTeleprompterScroll";
import { useMediaRecorder } from "./hooks/useMediaRecorder";

import React, { useMemo, useRef, useState } from "react";
import { Media, VideoScript } from "@/services/bedrock";
import Webcam from "react-webcam";
import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { VideoScriptSelectionDialog } from "@/components/RecordSelectionDialog/VideoScriptSelectionDialog";
import { CoachSettings } from "@/interfaces";
import { RecordActionSection } from "@/containers/RecordVideo/components/RecordActionSection";

type Props = {
  initialScript?: string;
  onVideoScriptSelected?: (script: VideoScript) => void;
  onCancel: () => void;
  onDone: (media: Media, secondsRecorded: number) => void;
  onSwitchToScreenCapture?: () => void;
};

const RecordVideoFC: React.FC<Props> = ({
  initialScript = "",
  onVideoScriptSelected,
  onCancel,
  onDone,
  onSwitchToScreenCapture,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { isCoach, playerId } = useSocialCoachSession();
  const urls = isCoach ? URLS.coach : URLS.player;
  const { hasSetting } = usePlayerSettings(playerId);
  const hasScreenCapture = useMemo(() => {
    if (isCoach) return true;
    return hasSetting(CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE, true);
  }, [hasSetting, isCoach]);

  // 1. Settings state management
  const {
    speed,
    setSpeed,
    textSize,
    setTextSize,
    aspectRatio,
    setAspectRatio,
    bgType,
    setBgType,
    bgBlur,
    setBgBlur,
    bgColors,
    setBgColors,
    bgImage,
    setBgImage,
    bgLogo,
    setBgLogo,
    bgLogoStyle,
    setBgLogoStyle,
    hasPersistedSettings,
    setHasPersistedSettings,
    debouncedBgBlur,
    debouncedBgColors,
  } = useRecorderSettings();

  // 2. Shared Refs
  const scrollToRef = useRef<HTMLTextAreaElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // 3. Media Recorder core hooks
  const webcamRef = useRef<Webcam>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingLabel, setLoadingLabel] = useState<string>("Uploading");
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);

  const mimeType = getSupportedMimeType();
  const videoType = mimeType.split(";")[0];

  const {
    recordingState,
    previewUrl,
    displaySeconds,
    dataReady,
    isRecording,
    onStartRecording,
    onStopRecording,
    onStartOver,
    onDoneRecording,
    setRecorder,
  } = useMediaRecorder(
    webcamRef,
    canvasRef,
    bgType,
    mimeType,
    videoType,
    scrollToRef,
    setLoading,
    setLoadingProgress,
    setLoadingLabel,
    onDone
  );

  // 4. Teleprompter scrolling loop (must be called after useMediaRecorder to read isRecording)
  useTeleprompterScroll(speed, isRecording, scrollToRef);

  // 5. Device management state management
  const {
    camera,
    setCamera,
    confirmingCamera,
    setConfirmingCamera,
    microphone,
    setMicrophone,
    confirmingMicrophone,
    setConfirmingMicrophone,
    cameraOptions,
    microphoneOptions,
    videoError,
    setVideoError,
    withPermissions,
    setWithPermissions,
    mountingNewWebcam,
    setMountingNewWebcam,
    loadingWebcam,
    setLoadingWebcam,
    refreshDevices,
    confirmCamera,
    confirmMic,
    updateCamera,
    updateMic,
    maxDeviceHeight,
    resolutionWarning,
    videoConstraints,
    audioConstraints,
    showConfirmationModal,
    setShowConfirmationModal,
  } = useDeviceManager(aspectRatio, recordingState, setHasPersistedSettings, onStartOver, setRecorder);

  // 6. Virtual Background processing engine
  useVirtualBackground(
    canvasRef,
    bgType,
    bgBlur,
    bgColors,
    bgImage,
    bgLogo,
    bgLogoStyle,
    debouncedBgBlur,
    debouncedBgColors,
    camera,
    withPermissions,
    webcamRef
  );

  // Custom states for layouts
  const [scriptMessage, setScriptMessage] = useState(initialScript);
  const [showScript, setShowScript] = useState(initialScript.length > 0);
  const [videoScriptModalOpen, setVideoScriptModalOpen] = useState(false);
  const [viewSwitched, setViewSwitched] = useState(false);

  // UI Derived states
  const isVertical = useMemo(() => aspectRatio === 9 / 16, [aspectRatio]);

  const showPreview = useMemo(() => {
    return previewUrl && previewUrl.length > 0 && !isRecording && dataReady && recordingState !== "toStart";
  }, [previewUrl, isRecording, dataReady, recordingState]);

  const hideWebcam = useMemo(() => {
    return !withPermissions || !camera || !microphone || showPreview;
  }, [camera, microphone, withPermissions, showPreview]);

  const showVideoLoader = useMemo(() => {
    return mountingNewWebcam || loadingWebcam || (hideWebcam && !showPreview && withPermissions !== false);
  }, [hideWebcam, showPreview, withPermissions, mountingNewWebcam, loadingWebcam]);

  const handleResetSettings = () => {
    const keys = [
      "scRecorderSpeed",
      "scRecorderTextSize",
      "scRecorderAspectRatio",
      "scRecorderBgType",
      "scRecorderBgBlur",
      "scRecorderBgColors",
      "scRecorderBgImage",
      "scRecorderPreferredCamera",
      "scRecorderPreferredMic",
    ];
    keys.forEach(key => localStorage.removeItem(key));

    setSpeed(15);
    setTextSize(24);
    setAspectRatio(9 / 16);
    setBgType("none");
    setBgBlur(8);
    setBgColors(["#3E4044", "#1A1B1C"]);
    setBgImage(null);
    setCamera(undefined);
    setMicrophone(undefined);
    setConfirmingCamera(undefined);
    setConfirmingMicrophone(undefined);

    setHasPersistedSettings(false);
  };

  const confirmStartOver = () => {
    setConfirmingMicrophone(undefined);
    setConfirmingCamera(undefined);
    setShowConfirmationModal(true);
  };

  const backAction = (): void => {
    if (isRecording) {
      if (window.confirm("Are you sure you want to discard this recording?")) {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  const ScriptSection = (
    <div className="w-full flex flex-col items-center gap-3">
      <div className="w-full text-center text-lg font-bold text-slate-800 dark:text-slate-200 select-none m-0">
        Script
      </div>
      <TeleprompterScript
        scriptMessage={scriptMessage}
        setScriptMessage={setScriptMessage}
        showScript={showScript}
        setShowScript={setShowScript}
        textSize={textSize}
        scrollToRef={scrollToRef}
        setVideoScriptModalOpen={setVideoScriptModalOpen}
      />
    </div>
  );

  const VideoSection = (
    <div className="w-full flex flex-col items-center gap-3">
      <div className="w-[315px] text-center text-lg font-bold text-slate-800 dark:text-slate-200 select-none m-0">
        {showPreview ? "Preview Video" : "Live Video"}
      </div>
      <VideoCanvas
        videoError={videoError}
        maxDeviceHeight={maxDeviceHeight}
        withPermissions={withPermissions}
        isVertical={isVertical}
        aspectRatio={aspectRatio}
        showPreview={!!showPreview}
        previewUrl={previewUrl}
        showVideoLoader={!!showVideoLoader}
        canvasRef={canvasRef}
        bgType={bgType}
        hideWebcam={!!hideWebcam}
        camera={camera}
        webcamRef={webcamRef}
        videoConstraints={videoConstraints}
        audioConstraints={audioConstraints}
        setVideoError={setVideoError}
        setWithPermissions={setWithPermissions}
        setMountingNewWebcam={setMountingNewWebcam}
        setLoadingWebcam={setLoadingWebcam}
        refreshDevices={refreshDevices}
        mountingNewWebcam={mountingNewWebcam}
      />
    </div>
  );

  return (
    <>
      <div className="w-full mx-auto flex flex-col gap-3.5">
        {/* Headers */}
        <div className="flex w-full justify-between items-center px-4 py-2 border-b border-gray-100 select-none">
          <div className="w-1/4 text-left">
            <Button
              variant="ghost"
              onClick={backAction}
              className="font-semibold text-gray-500 hover:text-gray-900 hover:bg-gray-100/80 rounded-lg px-3.5 h-9 flex items-center justify-center leading-none"
            >
              <ArrowLeft className="size-4 mr-2 shrink-0" /> Back
            </Button>
          </div>

          <RecordActionSection
            recordingState={recordingState}
            disabled={!withPermissions || (!!hideWebcam && !showPreview) || loadingWebcam || mountingNewWebcam}
            doneDisabled={!dataReady}
            startHandler={onStartRecording}
            stopHandler={onStopRecording}
            doneHandler={onDoneRecording}
            startOverHandler={confirmStartOver}
            secondsRecorded={displaySeconds}
          />
        </div>

        {/* Controls, script and video */}
        <div className="w-full max-w-[1500px] mx-auto flex flex-row gap-6 mt-0.5 items-start justify-center">
          {/* Controls */}
          <div className="w-[400px] shrink-0 flex flex-col items-center gap-3">
            <div className="w-full text-center text-lg font-bold text-slate-800 dark:text-slate-200 select-none m-0">
              Controls
            </div>
            <ControlSection
              speed={speed}
              textSize={textSize}
              onChangeSpeed={setSpeed}
              onChangeTextSize={setTextSize}
              onChangeCamera={confirmCamera}
              onChangeMicrophone={confirmMic}
              aspectRatio={aspectRatio}
              cameraOptions={cameraOptions}
              microphoneOptions={microphoneOptions}
              selectedCamera={camera}
              selectedMicrophone={microphone}
              disableCameraChanges={isRecording}
              canChangeAspectRatio={isRecording || displaySeconds > 0}
              onAspectRatioChange={setAspectRatio}
              warningMessage={resolutionWarning}
              bgType={bgType}
              bgBlur={bgBlur}
              bgColors={bgColors}
              bgImage={bgImage}
              bgLogo={bgLogo}
              onBgLogoChange={setBgLogo}
              bgLogoStyle={bgLogoStyle}
              onBgLogoStyleChange={setBgLogoStyle}
              onBgTypeChange={setBgType}
              onBgBlurChange={setBgBlur}
              onBgColorsChange={setBgColors}
              onBgImageChange={setBgImage}
              hasPersistedSettings={hasPersistedSettings}
              onResetSettings={handleResetSettings}
            />
          </div>

          {/* Script */}
          <div className="flex-1 max-w-[500px] flex flex-col items-center">
            {viewSwitched ? VideoSection : ScriptSection}
          </div>

          {/* Video */}
          <div className="w-[400px] shrink-0 relative flex flex-col items-center">
            <button
              type="button"
              onClick={() => !isRecording && setViewSwitched(!viewSwitched)}
              disabled={isRecording}
              className={`absolute -top-1.5 -left-[50px] p-2 rounded-full border border-gray-200 bg-white shadow-2xs hover:bg-gray-50 text-gray-500 hover:text-gray-900 transition-colors cursor-pointer select-none ${
                isRecording ? "opacity-50 cursor-not-allowed" : ""
              }`}
              title="Swap video and script view"
            >
              <ArrowRightLeft className="size-4" />
            </button>
            {viewSwitched ? ScriptSection : VideoSection}
          </div>
        </div>
      </div>

      <ScreenCaptureToggle
        isRecording={isRecording}
        hasScreenCapture={!!hasScreenCapture}
        recordingState={recordingState}
        onSwitchToScreenCapture={onSwitchToScreenCapture}
        history={history}
        urls={urls}
        location={location}
      />

      <UploadingOverlay loading={loading} loadingLabel={loadingLabel} loadingProgress={loadingProgress} />

      <ConfirmationModal
        title={"Warning! Are you sure?"}
        message={"This will restart the recording session, and lose all recorded progress."}
        openConfirmationModal={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        okHandler={() => {
          setShowConfirmationModal(false);
          if (confirmingCamera) {
            updateCamera(confirmingCamera, true);
          } else if (confirmingMicrophone) {
            updateMic(confirmingMicrophone, true);
          } else {
            onStartOver();
          }
        }}
        rejectHandler={() => {
          setShowConfirmationModal(false);
        }}
      />

      <VideoScriptSelectionDialog
        open={videoScriptModalOpen}
        onOpenChange={setVideoScriptModalOpen}
        title="Select a Video Script"
        description="Select a Video Script to use for your recording."
        onSelect={script => {
          setScriptMessage(script.content);
          setShowScript(true);
          setVideoScriptModalOpen(false);
          onVideoScriptSelected?.(script);
        }}
      />
    </>
  );
};

export const RecordVideo = RecordVideoFC;
